import { getProductDetailHref } from "@/libs/product/detail-href";
import { CartItem, useAddItem, useCartStore } from "@/store/cart-store";
// ⚠️ **정의 모듈에서 직접** 가져온다 — 배럴(`@/store/cart-store`)은 이 enum 을 re-export 만
// 하는데, 그 배럴을 `jest.mock` 으로 통째 모킹하는 테스트가 있어서 배럴 경유로 쓰면
// `AddToCartResult` 가 `undefined` 가 되고 **런타임에 터진다**(실측:
// `__tests__/store/game-bundle-redirect.test.tsx` 2건). 값이 필요한 import 는 모킹 표면을
// 피해서 잡는다.
import { AddToCartResult } from "@/store/cart-store/type";
import type { CartTrigger } from "@/libs/analytics/cart-trigger";
import { CartService } from "@/services/cart-service";
import { Product, ProductPrice, ProductSlug } from "@/api/product-api";
import { ProductUtil } from "@/utils/ProductUtil";
import { useCurrency } from "@/hooks/useCurrency";
import { useLocale } from "@/store/locale-store";
import { useMemo } from "react";
import { useProducts } from "@/services/product/useProductService";
import { useGameResellsKeyCounts } from "@/hooks/useGameResell";
import { EssentialGameItem } from "@/models/orders/OrderModelV2";
import {
  type CartGameLine,
  classifyCartGameItems,
  partitionCartGameLines,
} from "@/libs/cart/cart-game-stock";
import OrderService from "@/services/OrderService";
import { OrderItemV2 } from "@/models/orders/OrderDraft";
import { CurrencyIsoCode } from "@/enums";
import { OrderUtils } from "@/v2-components/pages/checkout/_utils/order-utils";
import {
  isReferralActiveForProduct,
  useReferralDiscount,
} from "@/hooks/useReferralDiscount";
import { useSellableGames } from "@/hooks/useBhapticsContents";

/**
 * 장바구니 게임 줄의 판매 가능 판정(`classifyCartGameItems`)에 넣을 입력을 모은다. 결과는 행 · 합계 · 결제 draft ·
 * 구매 불가 안내 · 결제 게이트가 **같이** 쓴다 — 그래서 `useCartWithProducts` 안에서 한 번만 부른다.
 *
 * - 판매 목록은 **국가로 거르지 않은 것**을 넘긴다(`avaliableCountryOnly: false`) — 국가 제외는 판정 함수가 사유로
 *   가른다. 거른 목록을 넘기면 그 나라에서 전부 제외된 경우 빈 목록 = 「모름」으로 읽혀 안내 없이 멈춘다.
 * - 키 재고는 **장바구니의 게임 id 만** 묻는다. 종전엔 행이 카탈로그 전체 id 의 응답(`useSellableGames({ inStockOnly })`)을,
 *   안내 다이얼로그가 장바구니 id 의 응답을 따로 봐서 두 응답이 엇갈리면 판정도 갈렸다. 장바구니에 게임이 없으면
 *   묻지 않는다(종전엔 이 훅을 부르는 모든 페이지가 카탈로그 전체의 재고를 물었다).
 * - `refetchKeyCounts` — 서버 결제 지점이 재고로 거절했을 때 그 키 재고를 지금 다시 묻는다(`refreshGameKeyStock`).
 */
const useCartGameLines = (
  gameItems: EssentialGameItem[],
): { lines: CartGameLine[]; refetchKeyCounts: () => unknown } => {
  const currency = useCurrency();
  const { countryCode } = useLocale();
  const catalog = useSellableGames({
    avaliableCountryOnly: false,
    // ⚠️ 장바구니에 게임이 있을 때만 다시 묻는다. 이 훅은 전역 구매 불가 안내(`_app.tsx` → `CartSheet`)를 통해 **모든
    // 페이지**에서 돌므로, 무조건 켜면 판매 목록 장애 때 빈 장바구니 방문자까지 탭마다 10초 간격으로 조른다.
    recheckOnError: gameItems.length > 0,
  });
  const gameResellIds = useMemo(
    () => Array.from(new Set(gameItems.map((item) => item.gameResellId))),
    [gameItems],
  );
  const keyCounts = useGameResellsKeyCounts(gameResellIds);

  const lines = useMemo(
    () =>
      classifyCartGameItems({
        items: gameItems,
        gameResells: catalog.gameResells,
        keyCounts: keyCounts.data,
        currency,
        countryCode,
      }),
    [gameItems, catalog.gameResells, keyCounts.data, currency, countryCode],
  );
  return { lines, refetchKeyCounts: keyCounts.refetch };
};

/**
 * 카트 항목 → `OrderItemV2`(합계 · 결제 draft · 분석이 쓰는 「청구」 목록).
 *
 * ★**현재 통화로 가격이 없는 항목은 건너뛴다**(`generateOrderItem` 이 `null`). 종전엔 그 함수가 throw 했고,
 * 이 계산은 렌더 중(`useMemo`)에 돌아서 **화면이 통째로 죽었다**(client-side exception). 재현: 미국에서 게임 키 번들
 * (EXOSHOCK · Arizona · Metro — KRW 가격 행이 `0` = 미등록)을 담고 국가를 한국으로 바꾼다. #5886(2026-10-01, 정가 0 =
 * 가격 없음) 이후로 `0` 이 null 이 되며 생긴 회귀다 — 그 전엔 ₩0 으로 계산돼 넘어갔다.
 *
 * 건너뛴 항목은 **ghost** 다 — `generateOrderItem` 의 `null` 은 `CartService.getUnresolvedItems` 와 **같은 술어**
 * (`ProductUtil.getProductPrice` null)라, 장바구니가 그 항목을 이용 불가로 따로 보여 주고(삭제 유도) 결제를 막는다(`unresolvedItems`). 합계에서 빠지는
 * 것도 그 행이 가격 없이 렌더되는 기준(`CartItem` 의 `if (!price) return null`)과 같다.
 */
export const buildCartOrderItems = ({
  items,
  products,
  currency,
  countryCode,
  referralDiscountPercentile,
}: {
  currency: CurrencyIsoCode;
  countryCode: string | null;
  items: CartItem[];
  products: Product[];
  referralDiscountPercentile: number;
}): OrderItemV2[] => {
  const productMap = new Map(products.map((p) => [p.slug, p]));

  return items.reduce((prev: OrderItemV2[], current) => {
    const product = productMap.get(current.slug);

    if (!product) return prev;

    const furtherDiscountPercentile = isReferralActiveForProduct(current.slug)
      ? referralDiscountPercentile
      : 0;

    const orderItem = OrderService.generateOrderItem({
      product: { ...product, ...current },
      currency,
      countryCode,
      furtherDiscountPercentile,
    });
    // ghost(현재 통화로 가격 없음) — 위 docblock
    if (!orderItem) return prev;

    return [
      ...prev,
      {
        ...orderItem,
      },
    ];
  }, []);
};

const useOrderItems = ({
  items,
  products,
  currency,
  countryCode,
  referralDiscountPercentile,
}: {
  currency: CurrencyIsoCode;
  countryCode: string | null;
  items: CartItem[];
  products: Product[];
  referralDiscountPercentile: number;
}): OrderItemV2[] =>
  useMemo(
    () =>
      buildCartOrderItems({
        items,
        products,
        currency,
        countryCode,
        referralDiscountPercentile,
      }),
    [products, items, currency, countryCode, referralDiscountPercentile],
  );

export const useCartWithProducts = (enabled: boolean = true) => {
  const { items, gameItems, showCart } = useCartStore((c) => ({
    items: c.items,
    gameItems: c.gameItems,
    showCart: c.showCart,
  }));
  const addItem = useAddItem();
  const currency = useCurrency();
  const { countryCode } = useLocale();
  const products = useProducts();
  const { referralDiscountPercentile } = useReferralDiscount();
  const { lines: gameLines, refetchKeyCounts } = useCartGameLines(gameItems);
  // `filledGameItems` 는 **판매 가능이 확인된** 게임만이다 — 합계 · 결제 draft · 분석 이벤트가 쓰는 「청구」 목록.
  // 「재고 확인 중」 줄은 행에만 보이고 여기 들어오지 않으며, 그 줄이 있는 동안 결제 진입점이 막힌다
  // (`checkingGameLines` · `allGameLinesAvailable`). 판정은 `@/libs/cart/cart-game-stock` 한 곳.
  const {
    availableGameItems: filledGameItems,
    shownLines: shownGameLines,
    checkingLines: checkingGameLines,
    unavailableLines: unavailableGameLines,
    allAvailable: allGameLinesAvailable,
  } = useMemo(() => partitionCartGameLines(gameLines), [gameLines]);
  const orderItems = useOrderItems({
    items,
    products: products.data ?? [],
    currency,
    countryCode,
    referralDiscountPercentile,
  });

  const productInfoList = CartService.getProductInfoList({
    cartItems: items,
    products: products.data ?? [],
  });

  // 현재 product 리스트에서 렌더 불가(ghost)한 카트 항목. 탐지 로직은 순수함수로 추출해
  // 단위테스트로 잠가 둠 (CartService.getUnresolvedItems). docs/pitfalls.md cart-ghost.
  const unresolvedItems = useMemo(
    () =>
      CartService.getUnresolvedItems({
        items,
        products: products.data ?? [],
        productsPending: products.isPending,
        currency,
        countryCode,
      }),
    [items, products.data, products.isPending, currency, countryCode],
  );

  const addCartBySlug = (
    slug: string,
    /** `add_to_cart` 진입점 (#5292). 호출처(=버튼이 놓인 표면)가 정한다. */
    trigger: CartTrigger = "unknown",
  ):
    | {
        bundles: Product[];
        /**
         * 이 값이 있으면 호출처가 **이동시킨다**(카트 시트를 열지 않는다).
         * `bundles` 분기보다 **먼저** 검사해야 한다 — `bundles: []` 이라 순서를 틀리면
         * 조용히 아무 일도 안 일어난다(#5348 리뷰 R1 Minor 1).
         */
        redirectTo?: string;
        /**
         * 담기가 **끝났을 때** 그 SKU. 호출처(`useAddItemToCart`)가 담기 완료 팝을 띄우는
         * 데 쓴다 (#5366). 종전에는 이 갈래가 아무것도 돌려주지 않아 **무음**이었다.
         *
         * ⚠️ `bundles`·`redirectTo` 와 **동시에 채워지지 않는다** — 그 둘은 "아직 안 담았고
         * 사용자에게 물어봐야 한다" 는 뜻이라 완료 신호와 상호배타다.
         */
        addedSku?: string;
      }
    | undefined => {
    const product = getProductBySlug(slug);

    if (!product) {
      showCart();
      return {
        bundles: [],
      };
    }

    /**
     * ★게임키를 포함한 번들은 **다이얼로그로 보내지 않고 PDP 로 보낸다** (#5286).
     *
     * 옵션 선택 다이얼로그(`SelectProductOption`)는 `gameResells: []` 로 SKU 를 찾으므로
     * 게임키 skuOption 은 **항상 매칭에 실패**한다(`getProductSkus` 의 `gameResellMatches`).
     * 그러면 본품이 조용히 빠지고 액세서리만 담긴다 — 오류도 안 뜬다.
     *
     * PDP 에는 `Select Game Key` 아코디언이 이미 있고, `/tactsuit/bundles` 의 카드도
     * (`NotifyOrBuy`) 같은 곳으로 보낸다. 즉 **두 경로가 같은 동작이 된다.**
     *
     * ⚠️ **후보 판정 앞에 둔다.** 뒤에 두면 후보가 있는 갈래
     * (`{ bundles: [product, ...candidateBundles] }` → `SelectRecommendedBundle`)로 빠져
     * 사용자가 그 번들을 고르는 순간 같은 다이얼로그가 열린다. 값이 아니라 **분기 위치**가
     * 이 수정의 핵심이다.
     *
     * ★이동을 **여기서 실행하지 않고 값으로 돌려준다.** 이 훅은 소비처가 23곳인데
     * `addCartBySlug` 를 쓰는 곳은 `useAddItemToCart` 하나다 — 여기서 `useRouter()` 를
     * 부르면 나머지 22곳에 라우터 컨텍스트 의존이 생긴다. 값이면 조건을 단위테스트로
     * 직접 칠 수 있다(#5348 리뷰 R1 Major/Minor 1·2).
     */
    if (CartService.hasGameKeySkuOption(product)) {
      return { bundles: [], redirectTo: getProductDetailHref(product.slug) };
    }

    const candidateBundles = CartService.getCandidateBundles({
      candidate: product,
      cartItems: items,
      products: products.data ?? [],
    });

    if (candidateBundles.length === 0) {
      if (product.skuOptions.length > 1) {
        return { bundles: [product] };
      } else {
        const sku = product.skuOptions[0].sku;
        const result = addItem(
          {
            slug: product.slug,
            quantity: 1,
            options: [],
            sku,
          },
          true,
          trigger,
        );
        // ★`addedSku` 는 **일반 담기에서만** 채운다 (#5366).
        //
        // `addItem` 은 세 갈래이고 **둘은 이미 자기 표면을 띄운다**. 반환을 안 보고
        // 무조건 채우면 그 위에 담기 완료 팝이 겹쳐서 **사실과 다른 말을 한다**:
        //   · `OverLimit`      담기지 **않았는데** "담았다" + Purchase Limit 과 동시 노출
        //   · `MergedToBundle` 번들로 묶였는데 **단품이 담겼다**고 말한다 + `/cart` 로
        //                      이동하는 중이라 팝이 GNB 에 남아 따라간다
        // 이 갈래는 `skuOptions.length === 1` 이라 SKU 가 하나로 확정된다.
        return {
          bundles: [],
          addedSku: result === AddToCartResult.Success ? sku : undefined,
        };
      }
    } else {
      return { bundles: [product, ...candidateBundles] };
    }
  };

  const getProductBySlug = (slug: string) =>
    products.data?.find((p) => p.slug === slug);

  const getProductsBySlug = (slugs: string[]) => {
    return CartService.getProductsBySlug({
      target: slugs as ProductSlug[],
      products: products.data ?? [],
    });
  };

  const getProductPrice = (product?: Product): ProductPrice | null => {
    if (!product) return null;
    const furtherDiscountPercentile = isReferralActiveForProduct(product.slug)
      ? referralDiscountPercentile
      : 0;

    return ProductUtil.getProductPrice({
      currency,
      countryCode,
      product,
      furtherDiscountPercentile,
    });
  };

  const getCartProductTagsBySlug = (slug: string): string[] | null => {
    const product = getProductBySlug(slug);
    const furtherDiscountPercentile = isReferralActiveForProduct(slug)
      ? referralDiscountPercentile
      : 0;
    // `prices` 직접 조회 금지 — 국가별 티어를 건너뛰어 EU/GB 에서 배지가 사라진다(§3.2).
    const price = product
      ? ProductUtil.getProductPrice({
          currency,
          countryCode,
          product,
          furtherDiscountPercentile,
        })
      : null;

    if (!product || !price) {
      return null;
    }

    if (!furtherDiscountPercentile) {
      return product.tags.slice(0, 2);
    }

    // ★배지 %는 `referralDiscountPercentile` 을 **그대로** 쓴다. 가격에서 역산하지 않는다.
    //
    // 종전엔 `price.discountAmount / price.originalAmount` 로 역산했는데, 분모가 어떤 가격
    // 티어냐에 따라 값이 흔들린다. `getPrice`(기준가) → `getProductPrice`(국가별 티어) 전환으로
    // US 는 분모가 `usPrice` 가 되면서 실제로 어긋났다 — 예: mesh-lining US 23 / 기준가 20 →
    // **10% 리퍼럴에 배지는 9%**. 10% 준다고 링크를 걸어놓고 다른 숫자를 보여주는 셈이다.
    //
    // 리퍼럴 할인율은 링크가 정하는 값이지 가격에서 파생되는 값이 아니다.
    // `useProductTags` 는 원래 이렇게 하고 있었다 — 두 배지 구현을 같은 규칙으로 맞춘다.
    return [
      `Ref. ${Math.round(furtherDiscountPercentile * 100)}%`,
      ...product.tags,
    ].slice(0, 2);
  };

  const isReferralDiscount = (slug: string) => {
    const product = getProductBySlug(slug);
    const furtherDiscountPercentile = isReferralActiveForProduct(slug)
      ? referralDiscountPercentile
      : 0;
    // 위와 동일 — 국가별 티어 경유 필수(§3.2).
    const price = product
      ? ProductUtil.getProductPrice({
          currency,
          countryCode,
          product,
          furtherDiscountPercentile,
        })
      : null;

    return !!furtherDiscountPercentile && !!product && !!price;
  };

  const priceSummary = useMemo(
    () =>
      OrderUtils.getPriceSummary({
        items: orderItems,
        gameItems: filledGameItems,
      }),
    [orderItems, filledGameItems],
  );

  const { productsTotalDiscount, productsTotalPrice } = useMemo(() => {
    return orderItems.reduce(
      (acc, item) => {
        acc.productsTotalPrice += item.price * item.qty;
        acc.productsTotalDiscount += item.discount * item.qty;
        return acc;
      },
      {
        productsTotalPrice: 0,
        productsTotalDiscount: 0,
      },
    );
  }, [orderItems]);

  const { gamesTotalDiscount, gamesTotalPrice } = useMemo(() => {
    return filledGameItems.reduce(
      (acc, item) => {
        acc.gamesTotalPrice += item.price;
        acc.gamesTotalDiscount += item.discount;
        return acc;
      },
      {
        gamesTotalPrice: 0,
        gamesTotalDiscount: 0,
      },
    );
  }, [filledGameItems]);

  const shippingType = useMemo(() => {
    return OrderService.getShippingType({
      items: orderItems,
      gameItems: filledGameItems,
    });
  }, [orderItems, filledGameItems]);

  const totalCount = useMemo(
    () => productInfoList.reduce((sum, item) => sum + (item.quantity || 1), 0),
    [productInfoList],
  );

  return {
    totalCount,
    shippingType,
    totalDiscount: priceSummary.totalDiscountAmount,
    totalPrice: priceSummary.totalOriginalPrice,
    totalSalePrice: priceSummary.totalSalePrice,
    // UI 상에서 고객에게 보여줄 할인금액
    displayTotalDiscountAmount: priceSummary.displayTotalDiscountAmount,
    // UI 상에서 고객에게 보여줄 가격
    displayTotalOriginalPrice: priceSummary.displayTotalOriginalPrice,
    // UI 상에서 고객에게 보여줄 최종가격
    displayTotalSalePrice: priceSummary.displayTotalSalePrice,
    productsTotalPrice,
    productsTotalDiscount,
    gamesTotalDiscount,
    gamesTotalPrice,
    orderItems,
    items: productInfoList,
    unresolvedItems,
    products: products.data ?? [],
    referralDiscountPercentile,
    isPending: products.isPending,
    gameItems: filledGameItems,
    /** 장바구니 행에 그리는 게임 줄(판매 가능 + 재고 확인 중, raw 카트 순서). */
    shownGameLines,
    /** 키 재고(또는 판매 목록)를 아직 모르는 게임 줄 — 결제 버튼 비활성 · 안내 문구 · 네이버페이 숨김. */
    checkingGameLines,
    /** 못 판다는 게 확인된 게임 줄 — 구매 불가 안내 다이얼로그가 띄우고 닫을 때 지운다. */
    unavailableGameLines,
    /**
     * 장바구니의 게임 줄이 **전부** 판매 가능으로 확인됐나 — 결제 진입 직전 이중 가드. GE 는 서버 카트(스토어의 게임 줄
     * 전부)를 읽으므로 `checking` 뿐 아니라 `unavailable` 줄도 남아 있으면 그대로 청구된다.
     */
    allGameLinesAvailable,
    /**
     * 장바구니 게임의 키 재고를 지금 다시 묻는다 — 서버 결제 지점이 재고로 거절했을 때
     * (`CHECKOUT_ERROR_CODE.GAME_KEY_STOCK_UNAVAILABLE`). 위 판정이 서버와 같은 값을 보게 해서 행의 「재고 확인 중」 ·
     * 구매 불가 안내가 이유를 보여준다.
     */
    refreshGameKeyStock: refetchKeyCounts,
    isReferralDiscount,
    addCartBySlug,
    getProductBySlug,
    getProductsBySlug,
    getProductPrice,
    getCartProductTagsBySlug,
  };
};

// Re-export for backward compatibility
export { useReferralDiscount } from "@/hooks/useReferralDiscount";
